import React, { useState, useEffect, useRef } from 'react';
import { 
  Box, 
  Typography, 
  TextField, 
  Button, 
  Alert, 
  CircularProgress,
  Paper,
  Link,
  Divider,
  Chip,
  IconButton,
  Tooltip
} from '@mui/material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import { 
  getUserProfile, 
  updateUserProfile, 
  getPSNLinkStatus,
  linkPSNAccount,
  unlinkPSNAccount,
  syncPSNLibrary,
  checkPSNSyncStatus,
  updateStatusCounts
} from '../../services/api';
import HourglassEmptyIcon from '@mui/icons-material/HourglassEmpty';
import AccessTimeIcon       from '@mui/icons-material/AccessTime';
import CheckCircleOutlineOutlinedIcon from '@mui/icons-material/CheckCircleOutlineOutlined';
import ErrorOutlineIcon     from '@mui/icons-material/ErrorOutline';
import Dialog from '../dialogs/Dialog';

const SyncStatusDisplay: React.FC<{
  status: number | null;
  message: string | null;
  lastSyncTime: string | null;
  nextSyncTime: string | null;
}> = ({ status, message, lastSyncTime, nextSyncTime }) => {
  if (status === null) return null;

  let icon: React.ReactNode;
  let label: string;
  let timeLabel: string | null = null;
  
  switch (status) {
    case 0:
      icon = <HourglassEmptyIcon color="disabled" />;
      label = '待機中';
      timeLabel = lastSyncTime ? `前回の同期: ${lastSyncTime} JST` : null;
      break;
    case 1:
      icon = <AccessTimeIcon color="info" />;
      label = 'キュー待ち';
      timeLabel = lastSyncTime ? `前回の同期: ${lastSyncTime} JST` : null;
      break;
    case 2:
      icon = <CircularProgress size={20} />;
      label = '処理中';
      timeLabel = lastSyncTime ? `前回の同期: ${lastSyncTime} JST` : null;
      break;
    case 3:
      icon = <CheckCircleOutlineOutlinedIcon color="success" />;
      label = '完了';
      timeLabel = lastSyncTime ? `同期完了: ${lastSyncTime} JST` : null;
      break;
    case 4:
      icon = <ErrorOutlineIcon color="error" />;
      label = '失敗';
      if (lastSyncTime) {
        const syncDate = new Date(lastSyncTime.replace(' JST', ''));
        const retryDate = new Date(syncDate.getTime() + 30 * 60 * 1000); // 30分後
        const now = new Date();
        
        if (now >= retryDate) {
          timeLabel = '再試行可能です';
        } else {
          const minutes = Math.ceil((retryDate.getTime() - now.getTime()) / (1000 * 60));
          timeLabel = `再試行まで約${minutes}分`;
        }
      }
      break;
    default:
      icon = null;
      label = '';
  }

  return (
    <Paper 
      variant="outlined" 
      sx={{ 
        p: 2, 
        mb: 2,
        bgcolor: status === 4 ? 'rgba(211, 47, 47, 0.04)' : 'inherit'
      }}
    >
      <Box display="flex" alignItems="center" justifyContent="space-between" gap={1}>
        <Box display="flex" alignItems="center" gap={1}>
          {icon}
          <Typography variant="body2" fontWeight="500" whiteSpace="nowrap">
            {label}
          </Typography>
        </Box>
        {message && (
          <Typography variant="caption" color="text.secondary">
            {message}
          </Typography>
        )}
      </Box>
      {timeLabel && (
        <Box mt={1}>
          <Typography variant="caption" color="text.secondary">
            {timeLabel}
          </Typography>
        </Box>
      )}
    </Paper>
  );
};

const PSNSettings: React.FC<{ isEarlyUser: boolean }> = ({ isEarlyUser }) => {
  const [psnId, setPsnId] = useState<string>('');
  const [verificationCode, setVerificationCode] = useState<string>('');
  const [loading, setLoading] = useState<boolean>(false);
  const [syncLoading, setSyncLoading] = useState<boolean>(false);
  const [success, setSuccess] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [hasPsnId, setHasPsnId] = useState<boolean>(false);
  const [updateLoading, setUpdateLoading] = useState<boolean>(false);
  const [lastUpdateTime, setLastUpdateTime] = useState<number>(0);
  const [nextSyncTime, setNextSyncTime] = useState<string | null>(null);
  const [lastSyncTime, setLastSyncTime] = useState<string | null>(null);
  const [canSync, setCanSync] = useState<boolean>(true);
  const [syncStatus, setSyncStatus] = useState<number | null>(null);
  const [syncStatusMessage, setSyncStatusMessage] = useState<string | null>(null);
  const [showPrivacyDialog, setShowPrivacyDialog] = useState<boolean>(false);
  const [showInstructionDialog, setShowInstructionDialog] = useState<boolean>(false);
  const pollingRef = useRef<NodeJS.Timeout | null>(null);

  const generateVerificationCode = () => {
    const characters = 'abcdefghijklmnopqrstuvwxyz0123456789';
    let result = '';
    for (let i = 0; i < 8; i++) {
      result += characters.charAt(Math.floor(Math.random() * characters.length));
    }
    return result;
  };

  useEffect(() => {
    const fetchProfile = async () => {
      try {
        setLoading(true);
        
        // PSN連携状況を取得
        const data = await getPSNLinkStatus();
        if (data.linked) {
          setPsnId(data.psn_id);
          setHasPsnId(true);
          checkSyncStatus();
        }
      } catch (err) {
        setError('PSN連携状況の取得に失敗しました');
      } finally {
        setLoading(false);
      }
    };

    fetchProfile();
    
    return () => {
      if (pollingRef.current) {
        clearInterval(pollingRef.current);
      }
    };
  }, []);

  const checkSyncStatus = async () => {
    try {
      const result = await checkPSNSyncStatus();
      setSyncStatus(result.status);
      setSyncStatusMessage(result.message);
      
      if (result.next_sync_time) {
        setNextSyncTime(result.next_sync_time);
      }
      
      if (result.last_sync_at) {
        const dt = new Date(result.last_sync_at);
        const formattedLast = dt.toLocaleString('ja-JP', {
          year: 'numeric', month: '2-digit', day: '2-digit',
          hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false
        }).replace(/\//g, '-').replace(/,/g, '');
        setLastSyncTime(formattedLast);
      }
      
      setCanSync(result.can_sync && result.status !== 2);
      
      if (result.status === 2 && !pollingRef.current) {
        pollingRef.current = setInterval(checkSyncStatus, 10000);
      } 
      else if (result.status !== 2 && pollingRef.current) {
        clearInterval(pollingRef.current);
        pollingRef.current = null;
      }
    } catch (err) {
      console.error('同期ステータスの確認に失敗:', err);
    }
  };

  const handlePSNLink = async () => {
    if (!psnId.trim() || !verificationCode.trim()) {
      setError('PSN IDと認証コードの両方を入力してください');
      return;
    }

    setLoading(true);
    setError(null);
    setSuccess(null);

    try {
      const data = await linkPSNAccount(psnId, verificationCode);
      setSuccess('PSN連携に成功しました');
      setHasPsnId(true);
      setVerificationCode('');
      checkSyncStatus();
    } catch (err: any) {
      setError(err.message || 'PSN連携に失敗しました');
    } finally {
      setLoading(false);
    }
  };

  const handleSyncLibrary = async () => {
    setShowPrivacyDialog(true);
  };

  const confirmSyncLibrary = async () => {
    setShowPrivacyDialog(false);
    setSyncLoading(true);
    setError(null);
    setSuccess(null);

    try {
      const data = await syncPSNLibrary();
      setSuccess('PSNライブラリの同期を開始しました');
      checkSyncStatus();
    } catch (err: any) {
      setError(err.message || 'ライブラリ同期の開始に失敗しました');
    } finally {
      setSyncLoading(false);
    }
  };

  const handleUnlinkPSN = async () => {
    if (!window.confirm('PSN連携を解除しますか？')) {
      return;
    }

    setLoading(true);
    setError(null);
    setSuccess(null);

    try {
      const data = await unlinkPSNAccount();
      setSuccess('PSN連携を解除しました');
      setHasPsnId(false);
      setPsnId('');
      setSyncStatus(null);
      setSyncStatusMessage(null);
      setLastSyncTime(null);
      setNextSyncTime(null);
    } catch (err: any) {
      setError(err.message || 'PSN連携の解除に失敗しました');
    } finally {
      setLoading(false);
    }
  };

  const handleGenerateCode = () => {
    setVerificationCode(generateVerificationCode());
  };

  const handleUpdateStatusCounts = async () => {
    const now = Date.now();
    if (now - lastUpdateTime < 2000) {
      return;
    }

    try {
      setUpdateLoading(true);
      setError(null);
      setSuccess(null);
      setLastUpdateTime(now);
      
      const [result] = await Promise.all([
        updateStatusCounts(),
        new Promise(resolve => setTimeout(resolve, 2000))
      ]);
      
      setSuccess('ステータスカウントが更新されました');
    } catch (err: any) {
      setError(err.message || 'ステータスカウントの更新に失敗しました');
    } finally {
      setUpdateLoading(false);
    }
  };

  if (loading && !hasPsnId) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" p={4}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Typography variant="h6" gutterBottom>
        PlayStation Network連携設定
      </Typography>

      <Paper elevation={0} sx={{ mb: 3, bgcolor: "background.paper" }}>
        <Typography variant="body2" color="text.secondary" paragraph>
          PSNアカウントを連携することで、トロフィー情報やゲームライブラリの同期が可能になります。また、PULSEへのログインにPSN認証を利用できるようになり、認証方法の紛失など万が一の際にもアカウントを保護することができます。
        </Typography>

                  <Box sx={{ mb: 3 }}>
          {hasPsnId ? (
            <>
              <Alert
                severity="success"
                sx={{
                  mb: 2,
                  maxWidth: 500,
                  background:
                    "linear-gradient(135deg, rgba(44, 62, 80, 0.95), rgba(0, 70, 213, 0.95))",
                  color: "#ffffff",
                  border: "1px solid rgba(0, 112, 255, 0.2)",
                  borderRadius: "8px",
                  backdropFilter: "blur(8px)",
                  "& .MuiAlert-icon": {
                    color: "#0070ff",
                  },
                }}
              >
                PSNアカウントが連携されています。PSN ID: {psnId}
              </Alert>
              <Box sx={{ display: 'flex', gap: 0, justifyContent: 'flex-end', mt: 0 }}>
                <Typography
                  component="button"
                  variant="caption"
                  onClick={handleUnlinkPSN}
                  disabled={loading}
                  sx={{
                    background: 'none',
                    border: 'none',
                    color: 'text.secondary',
                    cursor: loading ? 'default' : 'pointer',
                    textDecoration: 'underline',
                    fontSize: '0.7rem',
                    opacity: loading ? 0.5 : 1,
                    '&:hover': {
                      color: loading ? 'text.secondary' : 'error.main',
                    },
                  }}
                >
                  連携を解除
                </Typography>
              </Box>
            </>
          ) : (
            <Box>
              <TextField
                label="PSN ID"
                value={psnId}
                onChange={(e) => setPsnId(e.target.value)}
                fullWidth
                sx={{ mb: 2 }}
                helperText="PlayStation Networkのオンライン ID（表示名ではありません）"
                size="small"
              />
              
              <Box sx={{ mb: 2 }}>
                {(error || success) && (
                  <Alert 
                    severity={error ? "error" : "success"} 
                    sx={{ mb: 2 }}
                  >
                    {error || success}
                  </Alert>
                )}

                <TextField
                  label="認証コード"
                  value={verificationCode}
                  fullWidth
                  helperText="生成した認証コードをPSNプロフィールの自己紹介に記載してください"
                  size="small"
                  InputProps={{
                    readOnly: true,
                    endAdornment: verificationCode && (
                      <Tooltip title="コピー" placement="top">
                        <IconButton
                          size="small"
                          edge="end"
                          onClick={() => {
                            navigator.clipboard.writeText(verificationCode);
                            setSuccess('認証コードをコピーしました');
                            setTimeout(() => setSuccess(null), 2000);
                          }}
                          sx={{ 
                            color: 'text.secondary',
                            '&:hover': {
                              backgroundColor: 'action.hover'
                            }
                          }}
                        >
                          <ContentCopyIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    )
                  }}
                  sx={{
                    mb: 1,
                    '& .MuiInputBase-input': {
                      fontFamily: 'monospace',
                      letterSpacing: '0.1em',
                      fontSize: '0.9rem',
                    }
                  }}
                />
                
                <Box display="flex" gap={2} alignItems="center" sx={{ mb: 2 }}>
                  <Button 
                    variant="outlined"
                    onClick={handleGenerateCode}
                    size="small"
                    sx={{ 
                      minWidth: '100px',
                      color: 'text.secondary',
                      borderColor: 'divider',
                      '&:hover': {
                        borderColor: 'text.secondary',
                        backgroundColor: 'action.hover'
                      }
                    }}
                  >
                    コード生成
                  </Button>
                  
                  <Button
                    variant="text"
                    size="small"
                    onClick={() => setShowInstructionDialog(true)}
                    sx={{ 
                      textTransform: 'none',
                      color: 'text.secondary',
                      '&:hover': {
                        color: 'primary.main'
                      }
                    }}
                  >
                    認証手順を見る
                  </Button>
                </Box>
              </Box>

              <Button
                variant="contained"
                onClick={handlePSNLink}
                disabled={loading || !psnId.trim() || !verificationCode.trim()}
                startIcon={
                  <img 
                    src="/icons/service/playstation.svg" 
                    alt="PlayStation" 
                    style={{ width: '20px', height: '20px', filter: 'invert(1)' }}
                  />
                }
                sx={{ 
                  mb: 2,
                  background: 'linear-gradient(135deg, rgba(0, 70, 213, 0.95), rgba(44, 62, 80, 0.95))',
                  color: '#ffffff',
                  border: '1px solid rgba(0, 112, 255, 0.2)',
                  backdropFilter: 'blur(8px)',
                  '&:hover': {
                    background: 'linear-gradient(135deg, rgba(0, 70, 213, 1), rgba(44, 62, 80, 1))',
                    border: '1px solid rgba(0, 112, 255, 0.3)',
                  },
                  '&:disabled': {
                    background: 'linear-gradient(135deg, rgba(0, 70, 213, 0.3), rgba(44, 62, 80, 0.3))',
                    color: 'rgba(255, 255, 255, 0.3)',
                  }
                }}
                size="small"
              >
                {loading ? <CircularProgress size={24} /> : 'PSNで認証する'}
              </Button>
            </Box>
          )}
        </Box>

        <Divider sx={{ my: 3 }} />

        <Box>
          <Box display="flex" alignItems="center">
            <Typography variant="subtitle1">
              PSN Sync 
            </Typography>
            <Chip 
              label="BETA v0.1.0" 
              size="small" 
              color="default" 
              variant="outlined" 
              sx={{ 
                ml: 1, 
                height: 18, 
                borderColor: 'rgba(0, 112, 255, 0.5)',
                backgroundColor: 'rgba(255, 255, 255, 0.1)',
                borderRadius: '4px',
                fontSize: '11px',
                color: 'rgba(0, 112, 255, 1)',
              }} 
            />
          </Box>
          <Typography variant="body2" paragraph color="text.secondary">
            PSNのゲームをPULSEライブラリと同期します。プレイ状況に応じて自動でステータスが振り分けられますが、正確なステータスは手動で設定してください。 なお、本機能（現在のバージョン）において既存のゲームステータスが更新・削除されることはありません。
          </Typography>

          <SyncStatusDisplay
            status={syncStatus}
            message={syncStatusMessage}
            lastSyncTime={lastSyncTime}
            nextSyncTime={nextSyncTime}
          />

          {(success && success.includes('同期')) && (
            <Alert severity="info" sx={{ mb: 2 }}>
              {success}
            </Alert>
          )}

          {(error && error.includes('同期')) && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          <Button
            variant="outlined"
            color="primary"
            onClick={handleSyncLibrary}
            disabled={syncLoading || !hasPsnId || !canSync}
            startIcon={syncLoading ? <CircularProgress size={20} /> : null}
            sx={{ mt: 1 }}
            size="small"
          >
            {canSync ? 'ライブラリを同期' : syncStatus === 2 ? '同期中...' : 'ライブラリを同期'}
          </Button>

        </Box>

        <Divider sx={{ my: 3 }} />

        <Box>
          <Typography variant="subtitle1" gutterBottom>
            ステータスカウント修復ツール
          </Typography>

          <Typography variant="body2" paragraph color="text.secondary">
            ライブラリのステータスカウントが正しく集計されていない場合、手動で修復できます。
          </Typography>

          {(success && (success.includes('ステータス'))) && (
            <Alert severity="success" sx={{ mb: 2 }}>
              {success}
            </Alert>
          )}

          {(error && (error.includes('ステータス'))) && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          <Button
            variant="outlined"
            color="secondary"
            onClick={handleUpdateStatusCounts}
            disabled={updateLoading || Date.now() - lastUpdateTime < 2000}
            startIcon={updateLoading ? <CircularProgress size={20} /> : null}
            sx={{
              mt: 1,
              color: "text.secondary",
              borderColor: "divider",
              "&:hover": {
                backgroundColor: "action.hover",
                borderColor: "text.secondary",
              },
            }}
            size="small"
          >
            {updateLoading ? "更新中..." : "ライブラリを修復"}
          </Button>
        </Box>
      </Paper>

      <Dialog
        open={showPrivacyDialog}
        onClose={() => setShowPrivacyDialog(false)}
        title="ライブラリ同期の確認"
        content={
          <Box>
            <Typography variant="body1" gutterBottom>
              PSNライブラリの同期を開始します。
            </Typography>
            
            <Alert severity="info" sx={{ my: 2 }}>
              <Typography variant="body2">
                • 同期したくないゲームはPS側でトロフィー情報を「非公開」に設定してください
                <br />
                • PSNプロフィールのステータス、トロフィー情報、ゲームライブラリが同期されます
                <br />
                • 同期処理には数分かかる場合があります
              </Typography>
            </Alert>

            <Box display="flex" gap={2} justifyContent="flex-end" sx={{ mt: 3 }}>
              <Button onClick={() => setShowPrivacyDialog(false)} color="inherit" size="small">
                キャンセル
              </Button>
              <Button variant="contained" onClick={confirmSyncLibrary} size="small">
                同期を開始
              </Button>
            </Box>
          </Box>
        }
        showCloseButton={false}
        width={480}
      />

      <Dialog
        open={showInstructionDialog}
        onClose={() => setShowInstructionDialog(false)}
        title="PSN認証方法"
        content={
          <Box>
            <Typography variant="h6" gutterBottom>
              PSNアカウント認証手順
            </Typography>
            
            <Box sx={{ mb: 3 }}>
              <Typography variant="body1" gutterBottom sx={{ fontWeight: 'bold' }}>
                1. 認証コードの生成
              </Typography>
              <Typography variant="body2" sx={{ mb: 2, ml: 2 }}>
                上記の「生成」ボタンをクリックして認証コードを生成してください。
              </Typography>

              <Typography variant="body1" gutterBottom sx={{ fontWeight: 'bold' }}>
                2. PSNプロフィールの編集
              </Typography>
              <Typography variant="body2" sx={{ mb: 1, ml: 2 }}>
                以下の手順でPSNプロフィールに認証コードを追加してください：
              </Typography>
              <Box component="ul" sx={{ ml: 4, mb: 2 }}>
                <li>PlayStation.comにログイン</li>
                <li>プロフィール → プロフィールを編集</li>
                <li>「自己紹介」欄に生成した認証コードを追加</li>
                <li>変更を保存</li>
              </Box>

              <Typography variant="body1" gutterBottom sx={{ fontWeight: 'bold' }}>
                3. 連携の実行
              </Typography>
              <Typography variant="body2" sx={{ mb: 2, ml: 2 }}>
                PSN IDと認証コードを入力して「PSNで認証する」ボタンをクリックしてください。
              </Typography>
            </Box>

            <Alert severity="warning" sx={{ mb: 3 }}>
              <Typography variant="body2">
                <strong>重要事項：</strong>
                <br />
                • 認証後は自己紹介欄から認証コードを削除してかまいません
                <br />
                • PSNプロフィールのオンラインステータスとゲームプレイ情報が「フレンドまで」または「すべてのプレイヤー」に設定されている必要があります
                <br />
                • この機能はベータ版のため、予期しない動作やエラーが発生する可能性があります
              </Typography>
            </Alert>

            <Box display="flex" justifyContent="flex-end">
              <Button onClick={() => setShowInstructionDialog(false)} size="small">
                閉じる
              </Button>
            </Box>

          </Box>
        }
        showCloseButton={false}
        width={480}
      />
    </Box>
  );
};

export default PSNSettings; 